import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { CompositeModel } from '../complex/composite.model';
import { NumberModel } from '../primitive/number.model';
import { RatingModel } from '../primitive/rating.model';
import { TextModel } from '../primitive/text.model';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ConfigService } from '../../services/config/config.service';
import { isDefined } from '../../utilities/is-defined';

/**
 * This model is used for screens MaterialPickup and MaterialReturn to create array of materials
 */
export class MaterialPartSetModel extends CompositeModel {
    public awb: TextModel;
    public materialNumber: TextModel;
    public orderNumber: TextModel;
    public orderPosition: TextModel;
    public quantity: NumberModel;
    public feedbackRating?: RatingModel;
    public feedbackComment?: TextModel;
    public pickupRfid: TextModel;
    public returnLabel?: TextModel;

    public awbPackaging: TextModel;

    public constructor(
        awb = '',
        materialNumber = '',
        orderNumber = '',
        orderPosition = '',
        quantity: number | undefined = 1,
        pickupRfid = '',
        awbPackaging = '',
    ) {
        super('MaterialPartSet');

        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        this.awb = <TextModel>this.set(new TextModel('ReturnLabel', awb));
        this.materialNumber = <TextModel>this.set(new TextModel('Material', materialNumber));
        this.orderNumber = <TextModel>this.set(new TextModel('PONumber', orderNumber));
        this.orderPosition = <TextModel>this.set(new TextModel('POItemNumber', orderPosition));
        this.quantity = <NumberModel>this.set(new NumberModel('Quantity', quantity));

        if (configService.getPickupRFIDEnabled()) {
            this.pickupRfid = <TextModel>this.set(new TextModel('PickupRfid', pickupRfid));
            modelOptionsLoader.load(this.pickupRfid, 'MaterialPickup', 'UCRNumber');

            this.pickupRfid.options = {
                ...this.pickupRfid.options,
                readonly: pickupRfid.length > 0,
                required: false,
            };
        }

        if (configService.getEnableAWBPackaging()) {
            this.awbPackaging = <TextModel>this.set(new TextModel('PkgAwbnr', awbPackaging));
        }
    }

    public getOutModel(): ODataMaterialPartSetOutModel {
        const outModel = <ODataMaterialPartSetOutModel>super.getOutModel();

        // For bootstock return, returnLabel is set externally with the AWB return label value.
        if (isDefined(this.returnLabel)) {
            outModel.ReturnLabel = this.returnLabel.value;
        }

        // convert number to string as expected by gateway
        outModel.Quantity = outModel.Quantity ? outModel.Quantity.toString() : '';

        return outModel;
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {any}
     */
    public toJSON(): unknown {
        return {
            awb: this.awb.value,
            materialNumber: this.materialNumber.value,
            orderNumber: this.orderNumber.value,
            orderPosition: this.orderPosition.value,
            quantity: this.quantity.value,
            feedbackRating: this.feedbackRating ? this.feedbackRating.value : undefined,
            feedbackComment: this.feedbackComment ? this.feedbackComment.value : undefined,
            awbPackaging: this.awbPackaging ? this.awbPackaging.value : undefined,
            pickupRfid: this.pickupRfid ? this.pickupRfid.value : undefined,
            returnLabel: isDefined(this.returnLabel) ? this.returnLabel.value : undefined,
        };
    }
}

export interface ODataMaterialPartSetOutModel extends ODataObject {
    ReturnLabel: string;
    Material: string;
    PONumber: string;
    POItemNumber: string;
    Quantity: string;
    FeedbackRating: string;
    FeedbackComment: string;
    PkgAwbnr: string;
    PickupRfid: string;
}
